import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, Router } from '@angular/router';
import Point from '@arcgis/core/geometry/Point';
import { Message } from 'primeng/api';
import { Observable, of, Subject } from 'rxjs';
import { CrimeGunMapComponent } from 'src/app/crime-guns/crime-gun-detail/crime-gun-map/crime-gun-map.component';
import { LocationService } from 'src/app/services/location.service';
import { LookupService } from 'src/app/services/lookup.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AddressAttributes, AddressLocation, AddressReverseGeocode, Candidate } from 'src/app/shared/models/location-models';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { VerifyLocationComponent } from '../verify-location/verify-location.component';

@Component({
  selector: 'ness-location-form',
  templateUrl: './location-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationFormComponent implements OnDestroy {
  @Input() isEditForm: boolean = false;
  @Input() isEventLocationForm: boolean = false;
  @Input() isPersonAddressForm: boolean = false;
  @Input() associationButtonLabel: string = null;
  @Output() associateLocation: EventEmitter<ServerModels.ENESS_AddressDTO> = new EventEmitter<ServerModels.ENESS_AddressDTO>();
  @Output() locationSaved: EventEmitter<ServerModels.ENESS_AddressDTO> = new EventEmitter<ServerModels.ENESS_AddressDTO>();
  @Output() closeForm = new EventEmitter();

  @ViewChild(VerifyLocationComponent) verifyLocationCmp: VerifyLocationComponent;
  @ViewChild(CrimeGunMapComponent) crimeGunMapCmp: CrimeGunMapComponent;

  locationAddress: any[] = [];
  errorMessage: string;
  msgs: Message[] = [];
  isStandaloneForm: boolean = false;

  selectedAddress$: Observable<any[]> = of([]);

  latitudeRegEx: RegExp = /^[-]?([1-8]?\d(?:\.[0-9]{1,80})?|90(\.0+))?$/;
  longitudeRegEx: RegExp = /^[-]?(180(\.0{1,80})?|((1[0-7]\d)|([1-9]?\d))(\.\d{1,80}))?$/;

  mapPinLocation: AddressLocation;

  locationInputForm: FormGroup = this.fb.group({
    AddressID: null,
    LocationTypeID: '',
    StreetAddress: [null, [Validators.required, Validators.maxLength(80)]],
    Unit: null,
    CityName: [null, Validators.required],
    StateID: ['', Validators.required],
    PostalCode: [null, [Validators.required, Validators.pattern('[0-9]{5}')]],
    PostalCodeExtended: [null, [Validators.pattern('[0-9]{4}')]],
    CountryID: null,
    CapturedLongitude: { value: null, disabled: true },
    CapturedLatitude: { value: null, disabled: true },
    EnteredLatitude: [null, [NESSValidators.conditionalRequired(() => this.locationInputForm.get('CapturedLatitude').value == null), Validators.maxLength(80), NESSValidators.longLatPatternValid(this.latitudeRegEx)]],
    EnteredLongitude: [null, [NESSValidators.conditionalRequired(() => this.locationInputForm.get('CapturedLongitude').value == null), Validators.maxLength(80), NESSValidators.longLatPatternValid(this.longitudeRegEx)]],
    ConsolidatedAddressReturned: [{ value: null, disabled: true }, Validators.maxLength(80)],
    PoliceDepartment: [null, Validators.maxLength(80)],
    PoliceDepartmentSubArea1: [null, Validators.maxLength(80)],
    PoliceDepartmentSubArea2: [null, Validators.maxLength(80)],
    AnalystNotes: [null, Validators.maxLength(2000)]
  });
  locationTypes: ServerModels.ENESS_REF_LocationTypes[] = [];
  showConfirmEdit: boolean = false;
  textAreaRows: number = AppConstants.defaultTextAreaRows;

  private _titleText: string = 'Add a new location';
  @Input() set titleText(title: string) {
    this._titleText = title;
  }
  get titleText(): string {
    return this._titleText;
  }

  private _searchLocation: string;
  get searchLocation(): string {
    return this._searchLocation;
  }

  @Input() set searchLocation(searchValue: string) {
    if (!!searchValue && searchValue.indexOf("&amp;") > 0) searchValue = searchValue.replace("&amp;", "&"); // D-17882
    this._searchLocation = new String(searchValue || '').trim();
  }

  private _locationFormData: ServerModels.ENESS_AddressDTO;
  @Input() set locationFormData(address: ServerModels.ENESS_AddressDTO) {
    this._locationFormData = address;
    this.searchLocation = this.buildConsolidatedAddress(address);
    this._updateFormValues({
      AddressID: address.AddressID,
      LocationTypeID: address.LocationTypeID || '',
      StreetAddress: address.StreetAddress,
      Unit: address.Unit,
      CityName: address.CityName,
      StateID: address.StateID,
      PostalCode: address.PostalCode,
      PostalCodeExtended: address.PostalCodeExtended,
      CountryID: address.CountryID,
      CapturedLongitude: address.CapturedLongitude,
      CapturedLatitude: address.CapturedLatitude,
      EnteredLatitude: address.EnteredLatitude?.trim() || '',
      EnteredLongitude: address.EnteredLongitude?.trim() || '',
      ConsolidatedAddressReturned: address.ConsolidatedAddressReturned,
      PoliceDepartment: address.PoliceDepartment,
      PoliceDepartmentSubArea1: address.PoliceDepartmentSubArea1,
      PoliceDepartmentSubArea2: address.PoliceDepartmentSubArea2,
      AnalystNotes: address.AnalystNotes
    });
  }

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder,
    public locationService: LocationService,
    public lookupService: LookupService,
    private router: Router,
    private route: ActivatedRoute,
    public elementRef: ElementRef,
    private previouseRouteService: PreviousRouteService) {

    const transferData: Data = this.previouseRouteService.getTransferData();

    if (transferData && Object.keys(transferData).length) {
      this.isEditForm = transferData['isEditForm'] || false;
      this.isEventLocationForm = transferData['isEventLocationForm'] || false;
      this.isPersonAddressForm = transferData['isPersonAddressForm'] || false;
    }

    this.isStandaloneForm = this.route.component['name'] === 'LocationFormComponent'; 
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.clearData();
  }

  public clearData(): void {
    this.locationInputForm.reset({
      StateID: '',
      LocationTypeID: ''
    });

    this._locationFormData = null;
    this.errorMessage = null;
    this.msgs = [];
  }

  public onAddressVerified(address: Candidate): void {
    if (address) {
      let addresStr = this._buildStreetAddress(address.attributes);
      const location = {
        AddressID: this.locationInputForm.get('AddressID').value,
        LocationTypeID: this.locationInputForm.get('LocationTypeID').value,
        StreetAddress: addresStr,
        Unit: this.locationInputForm.get('Unit').value ?? '',
        CityName: address.attributes.City,
        StateID: this.lookupService.usStatesSubj.getValue().find((state: ServerModels.ENESS_REF_State) => state.StateAbbreviation.toUpperCase() === address.attributes.RegionAbbr.toUpperCase() || state.StateName.toUpperCase() === address.attributes.Region.toUpperCase()).StateID,
        PostalCode: address.attributes.Postal,
        PostalCodeExtended: this.locationInputForm.get('PostalCodeExtended').value ?? '',
        CountryID: this.locationInputForm.get('CountryID').value ?? '',
        CapturedLongitude: address.location.x,
        CapturedLatitude: address.location.y,
        EnteredLatitude: this.locationInputForm.get('EnteredLatitude').value ?? '',
        EnteredLongitude: this.locationInputForm.get('EnteredLongitude').value ?? '',
        ConsolidatedAddressReturned: address.address,
        PoliceDepartment: this.locationInputForm.get('PoliceDepartment').value ?? '',
        PoliceDepartmentSubArea1: this.locationInputForm.get('PoliceDepartmentSubArea1').value ?? '',
        PoliceDepartmentSubArea2: this.locationInputForm.get('PoliceDepartmentSubArea2').value ?? '',
        AnalystNotes: this.locationInputForm.get('AnalystNotes').value ?? ''
      }

      this.locationAddress = [];
      this.locationAddress.push(location);

      this.selectedAddress$ = of(this.locationAddress);
    }
  }

  public onMaskBlur(field: string): void {
    let ctrl = this.locationInputForm.get(field);
    if (ctrl.invalid) {
      ctrl.reset();
    }
  }

  public onSaveLocation(): void {
    this._setLocationFormData();
    if (this.isEditForm && this._locationFormData.AddressID == null) {
      this.locationService.addOrUpdateAddressStaging(this._locationFormData).subscribe((result: ServerModels.ENESS_AddressDTO) => {
        this.associateLocation.emit(this._locationFormData);
        this.clearData();
      },
        (err:any) => {
          this.errorMessage = err.error || err;
          this.msgs = [{ key: 'locationFormMsg', severity: 'error', summary: 'Error', detail: this.errorMessage }];
        }
      );
    }
    else {
      this.onCloseConfirmation();
      this.locationService.addOrUpdateLocation(this._locationFormData).subscribe((result:string) => {
        if (this.isEditForm) {
          this.locationSaved.emit(this._locationFormData);
          this.clearData();
          this.onClose();
        }
        else {
          this.clearData();
          this.router.navigate(['/locations']);
        }
      },
        (err:any) => {
          this.errorMessage = err.error || err;
          this.msgs = [{ key: 'locationFormMsg', severity: 'error', summary: 'Error', detail: this.errorMessage }];
        }
      );
    }
  }

  public onAssociateLocation(): void {
    this._setLocationFormData();
    this.locationService.addOrUpdateLocation(this._locationFormData).subscribe((result: string) => {
      this._locationFormData.AddressID = result;
      this.associateLocation.emit(this._locationFormData);
      this.clearData();
    },
      (err:any) => {
        this.errorMessage = err.error || err;
        this.msgs = [{ key: 'locationFormMsg', severity: 'error', summary: 'Error', detail: this.errorMessage }];
        this.clearData();
      }
    );
  }

  public mapPinMoved(value: Point): void {
    this.mapPinLocation = {
      x: value.latitude.toString(),
      y: value.longitude.toString()
    };

    this.locationService.getAddressByReverseGeocode(value.latitude.toString(), value.longitude.toString()).subscribe((result:string) => {

      const reverseGeocode: AddressReverseGeocode = JSON.parse(result)
      let streetAddy: string = this.verifyLocationCmp.locationVerifyForm.get('Address').value.text;
      let addyParts: string[] = streetAddy.split(',');
      // Remove unit from address parts
      var unit = this.locationInputForm.get('Unit').value;
      if (unit) {
        let unitToRemove = unit.trim();
        addyParts = addyParts.map(item => item.trim()).filter(item => item !== unitToRemove);
      }

      const location = {
        AddressID: this.locationInputForm.get('AddressID').value,
        LocationTypeID: this.locationInputForm.get('LocationTypeID').value,
        StreetAddress: streetAddy.substr(0, streetAddy.indexOf(',')),
        Unit: this.locationInputForm.get('Unit').value ?? '',
        CityName: addyParts[1].trim(),
        StateID: this.lookupService.usStatesSubj.getValue().find((state: ServerModels.ENESS_REF_State) => state.StateAbbreviation.toUpperCase() === reverseGeocode.address.RegionAbbr.toUpperCase() || state.StateName.toUpperCase() === reverseGeocode.address.Region.toUpperCase()).StateID,
        PostalCode: addyParts[3].trim(),
        PostalCodeExtended: this.locationInputForm.get('PostalCodeExtended').value ?? '',
        CountryID: this.locationInputForm.get('CountryID').value ?? '',
        CapturedLongitude: reverseGeocode.location.x,
        CapturedLatitude: reverseGeocode.location.y,
        EnteredLatitude: this.locationInputForm.get('EnteredLatitude').value ?? '',
        EnteredLongitude: this.locationInputForm.get('EnteredLongitude').value ?? '',
        ConsolidatedAddressReturned: streetAddy,
        PoliceDepartment: this.locationInputForm.get('PoliceDepartment').value ?? '',
        PoliceDepartmentSubArea1: this.locationInputForm.get('PoliceDepartmentSubArea1').value ?? '',
        PoliceDepartmentSubArea2: this.locationInputForm.get('PoliceDepartmentSubArea2').value ?? '',
        AnalystNotes: this.locationInputForm.get('AnalystNotes').value ?? ''
      }

      this.locationAddress = [];
      this.locationAddress.push(location);
      this.selectedAddress$ = of(this.locationAddress);
      this.crimeGunMapCmp.pinnedLocation = reverseGeocode;
    },
      (err:any) => {
        this.crimeGunMapCmp.pinnedLocation.location = this.mapPinLocation;
      });
  }

  private _setLocationFormData(): void {
    if (this.locationInputForm.valid) {
      this._locationFormData = Object.assign((this._locationFormData || {}), <ServerModels.ENESS_AddressDTO>this.locationInputForm.getRawValue());
      if (this._locationFormData.StateID) {
        let locationState: ServerModels.ENESS_REF_State = this.lookupService.usStatesSubj.getValue().find((s: ServerModels.ENESS_REF_State) => s.StateID.toLowerCase() === this._locationFormData.StateID.toLowerCase());
        this._locationFormData.StateName = locationState.StateName;
        this._locationFormData.StateAbbreviation = locationState.StateAbbreviation;
        this._locationFormData.ConsolidatedAddressReturned = this.buildConsolidatedAddress(this._locationFormData);
      }
      if (this._locationFormData.LocationTypeID) {
        let locationType: ServerModels.ENESS_REF_LocationTypes = this.locationService.locationTypesSubj.getValue().find((l: ServerModels.ENESS_REF_LocationTypes) => l.LocationTypeID.toLowerCase() === this._locationFormData.LocationTypeID.toLowerCase());
        this._locationFormData.LocationType = locationType?.LocationType;
      }
    }
  }

  private _buildStreetAddress(attributes: AddressAttributes): string {
    let addressString: string = `${attributes.AddNum.trim()} ${attributes.StPreDir.trim()} ${attributes.StName.trim()} ${attributes.StType.trim()} ${attributes.StDir.trim()}`;
    addressString = addressString.replace(/\s{2}/g, " ");

    if (addressString.trim() == "") {
      addressString = attributes.Match_addr.split(",")[0];
    }
    return addressString;
  }

  public confirmAddress() {
    this._updateFormValues(this.locationAddress[0]);
    this.verifyLocationCmp.clearAllFields();
    this.selectedAddress$ = of([]);
  }

  private _updateFormValues(location: any): void {
    this.locationInputForm.setValue(location);
    this.locationInputForm.updateValueAndValidity();
  }

  public stripWhiteSpace(formControl: string): void {
    this.locationInputForm.get(formControl).setValue(this.locationInputForm.get(formControl).value.trim());
  }

  private buildConsolidatedAddress(location: ServerModels.ENESS_AddressDTO) {
    let addressParts: string[] = [location.StreetAddress?.trim(), location.Unit?.trim(), location.CityName?.trim(), location.StateAbbreviation?.trim(), location.PostalCode?.trim()]
    return addressParts.filter(a => a).join(", ").replace(/\s\s+/g, ' ');
  }

  onOpenConfirmation(): void {
    this.showConfirmEdit = true;
  }

  onCloseConfirmation(): void {
    this.showConfirmEdit = false;
  }

  onClose(): void {
    this.showConfirmEdit = false;
    this.closeForm.emit();
  }

}
